import { Box } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import Filter from 'components/Filter/Filter';
import { Loader } from 'components/Forms';
import SearchToolbar from 'components/Forms/SearchToolbar';
import { ActivitiesTableColumnKeys } from 'components/Partners/ActivitiesTable/activitiesHeaderColumns';
import ActivitiesTable from 'components/Partners/ActivitiesTable/ActivitiesTable';
import PartnerSkeleton from 'components/Partners/Skeleton/PartnerSkeleton';
import {
  ActionContainer,
  EmptySearchResults,
  ErrorState,
  InfiniteScrollWrapper,
  PageContainer,
  PageTitle,
} from 'components/Structure';
import { CardIllustration, EmptyStateCard, InfoTooltip, ThemeButton } from 'designSystem';
import useActivities from 'hooks/useActivities';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { AvailableSizes, BackgroundTypes, IllustrationTypes, ItemTypes } from 'types/enums';

const Activities: FC = () => {
  const {
    activities,
    loading,
    error,
    filters,
    searchTerm,
    hasNextPage,
    handlePageEndReached,
    handleSearchTermChange,
    handleCreateActivity,
    handleEditActivity,
    handleFilterChange,
    handleFilterReset,
  } = useActivities();
  const navigate = useNavigate();

  if (error) return <ErrorState />;

  if (!activities.length && !searchTerm && !filters.some(filter => !!filter.value) && !loading) {
    return (
      <PageContainer>
        <PageTitle
          title="Activities"
          TitleAdornment={
            <Box ml={1}>
              <InfoTooltip
                size={AvailableSizes.LARGE}
                text="Manage the activities of your own company, or view and manage all activities from different partners in your supply chains."
              />
            </Box>
          }
        />

        <Box mt={4}>
          <EmptyStateCard
            data-testid="activities-getting-started-card"
            data-testid-button="activities-getting-started-card-button"
            title="Add your activities to map your supply chain and promote transparency"
            variant={ItemTypes.LOCATION}
            buttonText="Add activity"
            background={BackgroundTypes.WORLD}
            size={AvailableSizes.LARGE}
            renderIllustration={
              <CardIllustration
                image={IllustrationTypes.LOCATIONS}
                width={300}
                right={25}
                top={0}
              />
            }
            onCreateItemClick={handleCreateActivity}
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Activities"
        TitleAdornment={
          <Box ml={1}>
            <InfoTooltip
              size={AvailableSizes.LARGE}
              text="Manage the activities of your own company, or view and manage all activities from different partners in your supply chains."
            />
          </Box>
        }
      />
      <Box mb={3} />

      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <ActionContainer>
        <SearchToolbar
          setSearchTerm={handleSearchTermChange}
          searchTerm={searchTerm || ''}
          className="search-input"
          data-cy="activity-search-input"
          placeholder="Search activity"
          actions={
            <Box ml={1}>
              <ThemeButton
                size="medium"
                color="YELLOW"
                onClick={handleCreateActivity}
                startIcon={<Plus size={20} />}
                data-cy="create-activity-btn"
              >
                Create new activity
              </ThemeButton>
            </Box>
          }
        />
      </ActionContainer>

      {loading && <Loader />}

      {activities.length > 0 && !loading && (
        <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
          <ActivitiesTable
            columns={[
              ActivitiesTableColumnKeys.TITLE,
              ActivitiesTableColumnKeys.PARTNER,
              ActivitiesTableColumnKeys.MODIFIED_TIMESTAMP,
              ActivitiesTableColumnKeys.ACTIONS,
            ]}
            rowsPerPage={Infinity} // TODO: Remove pagination and use infinite scroll everywhere
            activities={activities}
            onSelect={({ id }) => navigate(`/activities/${id}`)}
            onEdit={handleEditActivity}
          />
        </InfiniteScrollWrapper>
      )}

      {!activities.length && !loading && (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'Create new activity',
            onClick: handleCreateActivity,
          }}
          skeletonComponent={<PartnerSkeleton title={searchTerm || ''} />}
        />
      )}
    </PageContainer>
  );
};

export default Activities;
